/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormState,
  FormStateBuilder,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, getRouteInitData, isRouteForCreationMode, UserContext } from '@gh/common-shared';
import { AutocompleteLoadMoreEvent, DialogService, FormMode, markControlAsDirty, setControlValue } from '@gh/core-ui';
import { isElementFocused, isNotNil } from '@gh/core-util';
import { SmsTemplateDetailsBusinessService } from '../../resources/sms-template-details-business.service';
import { SmsPlaceholder, SmsTemplateDetails } from '../../resources/sms-templates.model';
import { smsTemplatePreviewDialogRoute } from './sms-template-preview-dialog/sms-template-preview-dialog.component';

@Component({
  selector: 'gh-sms-template-details',
  template: require('./sms-template-details.component.html'),
  styles: [require('./sms-template-details.component.scss')],
})
export class SmsTemplateDetailsComponent extends AbstractFormComponent<SmsTemplateDetails> implements OnInit {
  form: FormState<SmsTemplateDetails>;
  isPlaceholderAutocompleteOpen: boolean;

  @ViewChild('body', { read: ElementRef, static: false }) body: ElementRef;
  @ViewChild('nameField') fieldToFocus!: ElementRef;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private dialogs: DialogService,
              private userContext: UserContext,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private formStateBuilder: FormStateBuilder,
              private business: SmsTemplateDetailsBusinessService) {
    super();
  }

  get pageTitle(): string {
    if (this.form.createMode) {
      return 'lbl_create_sms_template';
    } else if (this.form.editMode) {
      return 'lbl_edit_sms_template';
    } else {
      return 'lbl_sms_template';
    }
  }

  get completeFlow(): ActionFlow<FormFlowState<SmsTemplateDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<SmsTemplateDetails>) =>
        this.business.save(state.value)),
      finish: ({ value }) => {
        if (this.form.createMode) {
          this.router.switchTo(['/setup/sms-templates', value.id]);
        } else {
          this.form.setValue(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    const mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode
      : this.userContext.hasPermission(BuilderPermission.Setup.SmsTemplates.EDIT)
        ? FormMode.EditMode
        : FormMode.ViewMode;

    this.form = this.formStateBuilder.buildPlain({
      mode,
      value: details,
      optimisticLock: true,
      enabled: whenCreateOrEditMode(),
      children: {
        id: void 0,
        name: { required: true },
        description: void 0,
        body: { required: true },
      },
    });
  }

  ngAfterViewInit(): void {
    if(this.form.mode === 1)this.fieldToFocus.nativeElement.focus()
  }

  onShowPreview(): void {
    this.dialogs.openWithInput(smsTemplatePreviewDialogRoute, { template: this.form.submitValue });
  }

  onSelectPlaceholder(placeholder: SmsPlaceholder): void {
    const input: HTMLInputElement = this.body.nativeElement;
    // if element is focused or if it was touched earlier, then use selectionStart
    // otherwise always append text to the end
    const positionToInsert = isElementFocused(input) || isNotNil(input.selectionStart) && input.selectionStart !== 0
      ? (input.selectionStart || 0)
      : input.value.length;

    const { value } = input;

    const body = `${value.substring(0, positionToInsert)}$${placeholder.code} ${value.substring(positionToInsert)}`;
    // update input state via control, because otherwise state of the form will not be updated
    setControlValue(this.form.group, 'body', body);
    markControlAsDirty(this.form.group, 'body');
    input.focus();

    this.isPlaceholderAutocompleteOpen = false;
  }

  loadMorePlaceholders($event: AutocompleteLoadMoreEvent): void {
    const { query, load } = $event;
    load(this.business.findAllSmsPlaceholders(query));
  }
}
